import { useState, useEffect } from 'react'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import {
    FormDropDownList,
    FormInput,
    FormTextArea,
    FormTimePicker,
} from '../FormComponents'
import SkeletonForm from '../../Skeletons/SkeletonForm'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { useToast } from '../../../contexts/ToastContext'
import { checkTimesheetExists, createTimesheet, createTimesheetEntry, deleteTimesheetEntryById, getTimesheetEntryById, TimesheetEntry, updateTimesheetEntryById } from '../../../data/timesheetData'
import EventContainerList, { GridDataItem } from '../../Diary/EventContainerList'
import { JobDataItem } from '../../../pages/Diary/Diary'
import { getJobsForSchedulerList } from '../../../data/jobData';
import './TimesheetForm.css';
import { dropDown } from '../../../data/interfaces';

interface Props {
    id?: number | null
    mode: "edit" | "add"
    handleExpanded?: (fn?: () => void) => void
    getTableData?: () => void
    weekDays: dropDown[]
    employeeId: number | undefined
    /** Without edit access: look, don't change. */
    readOnly?: boolean
}

const TimesheetForm = (props: Props) => {
    const [jobDataUnscheduled, setJobDataUnscheduled] = useState<JobDataItem[]>([]);
    const [selectedJobItem, setSelectedJobItem] = useState<GridDataItem | null>(null);
    const [selectedDay, setSelectedDay] = useState<dropDown | undefined>(props.weekDays[0]);
    const [startTime, setStartTime] = useState<string>('');
    /** The selected item's full details */
    const [selectedItem, setSelectedItem] = useState<TimesheetEntry | undefined>(undefined);
    /** Are we loading the selected item from the API? */
    const [loading, setLoading] = useState(false);
    /** The selected tab in the sidebar */
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [currentTimeSheet, setCurrentTimeSheet] = useState<TimesheetEntry | undefined>(undefined);

    /** Toast hook to display toast */
    const { showToast } = useToast()

    const [mode, setMode] = useState<'edit' | 'add'>(props.mode);


    /** A blank from state */
    const blankFormState: TimesheetEntry = {
        timeSheetEntryId: 0,
        timeSheetId: 0,
        jobId: 0,
        timeSpent: 0,
        notes: '',
        clientName: '',
        description: '',
        jobReference: '',
        siteAddress: '',
        timeSheetDate: '',
    }
    /** Form state */
    const [formState, setFormState] = useState<TimesheetEntry>(blankFormState);

    /**
     * Retrieves the selected service reminder item by its ID and sets it as the selected item.
     * @returns {Promise<void>} A promise that resolves when the selected item is retrieved and set.
     */
    const getSelectedItem = async () => {
        if (props.mode === 'edit') {
            setLoading(true)
            if (props.id) {
                const item = await getTimesheetEntryById(props.id)
                setSelectedItem(item);
                setFormState(item)
            }
            setLoading(false)
        }
    }

    /**
     * Fetches non-scheduled events and updates the state with the retrieved data.
     *
     * This function calls `getJobsForSchedulerList` with parameters to fetch jobs
     * that are not scheduled. Once the data is retrieved, it updates the state
     * using `setJobDataUnscheduled`.
     *
     * @async
     * @function getNonScheduledEvents
     * @returns {Promise<void>} A promise that resolves when the data is fetched and state is updated.
     */
    const getNonScheduledEvents = async () => {
        const nonScheduledData = await getJobsForSchedulerList(false, false);
        setJobDataUnscheduled(nonScheduledData);
    }

    /**
     * Handles the click event when the save button is clicked.
     * Updates the service reminder with the new form state values.
     */
    const saveClickHandler = async () => {
        try {
            const res = await updateTimesheetEntryById({
                jobId: selectedJobItem?.jobId || formState.jobId,
                notes: formState.notes,
                timeSheetId: formState.timeSheetId,
                timeSpent: formState.timeSpent,
                timeSheetEntryId: formState.timeSheetEntryId
            })
            console.log(res)
            if(props.getTableData) {
                props.getTableData()
            }
            showToast('Timesheet entry updated', 'success')
        } catch (err) {
            showToast('Error updating timesheet entry', 'error')
            console.log(err)
        }
    }

    /**
     * Handles the click event for deleting a timesheet entry.
     * 
     * This function attempts to delete a timesheet entry by its ID. If the deletion is successful,
     * it logs the response, refreshes the table data if the `getTableData` prop is provided, and
     * shows a success toast message. If an error occurs during the deletion process, it logs the
     * error and shows an error toast message.
     * 
     * @async
     * @function deleteClickHandler
     * @returns {Promise<void>} A promise that resolves when the deletion process is complete.
     */
    const deleteClickHandler = async () => {
        try { 
            const res = await deleteTimesheetEntryById(formState.timeSheetEntryId)
            console.log(res)
            if(props.getTableData) {
                props.getTableData()
            }
            showToast('Timesheet entry deleted', 'success')
        } catch (err) {
            showToast('Error deleting timesheet entry', 'error')
            console.log(err)
        }
    }

    /**
     * Handles the click event to create a new account.
     * 
     * This function attempts to create a new account using the provided form state.
     * If the account creation is successful, it logs the response, refreshes the table data,
     * and shows a success toast message. If the account creation fails, it logs the error
     * and shows an error toast message.
     * 
     * @returns {Promise<void>} A promise that resolves when the account creation process is complete.
     */
    const createClickHandler = async (): Promise<void> => {
        if(selectedDay && props.employeeId) {
            try {

                if (!currentTimeSheet) { 
                    const createSheet = await createTimesheet({
                        date: selectedDay.data as string,
                        employeeId: props.employeeId,
                        startTime: startTime,
                    })
                    console.log(createSheet)
                    setCurrentTimeSheet(createSheet)

                    const createEntry = await createTimesheetEntry ({
                        jobId: selectedJobItem?.jobId || formState.jobId,
                        notes: formState.notes,
                        timeSheetId: createSheet.timeSheetId,
                        timeSpent: formState.timeSpent,
                    })
                    console.log(createEntry)
                    if(props.getTableData) {
                        props.getTableData()
                    }   

                    if(props.getTableData) {
                        props.getTableData()
                    }
                }

                if(currentTimeSheet) { 
                    const createEntry = await createTimesheetEntry ({
                        jobId: selectedJobItem?.jobId || formState.jobId,
                        notes: formState.notes,
                        timeSheetId: currentTimeSheet.timeSheetId,
                        timeSpent: formState.timeSpent,
                    })
                    if(props.getTableData) {
                        props.getTableData()
                    }                    
                    console.log(createEntry)
                }

            } catch(err) { 
                console.log(err)
            }
        }
    }

    const checkTimesheetExistsForDayAndEmployee = async () => {
        if(selectedDay) {
            if(selectedDay.data && props.employeeId) {
                try { 
                    const res = await checkTimesheetExists(props.employeeId, selectedDay.data);
                    if(res.length) {
                        setCurrentTimeSheet(res[0])
                    } else { 
                        setCurrentTimeSheet(undefined)
                    }
                } catch(err) { 
                    console.log(err)
                }
            }
        }
    }


    /**
     * Checks if the form is valid. Disabled the save button if the form is invalid.
     * @returns {boolean} True if the form is valid, false otherwise.
     */
    const isFormValid = (): boolean => {
        // if (1 + 2 === 3) {
        //     return true;
        // } else {
        //     return false;
        // }
        return false
    }


    /**
     * Get the selected item on load, if there's and ID and the mode is edit
     */
    useEffect(() => {
        getSelectedItem()
    }, [props.id, props.mode]);

    /**
     * If we are adding a new account, clear the state
     */
    useEffect(() => {
        if (props.mode === 'add') {
            setFormState(() => ({
                ...blankFormState,
            }));
        }
    }, [props.mode]);

    /**
     * Handles the form submission based on the mode (edit or add).
     * 
     * - If the mode is 'edit', it calls the `saveClickHandler` function and then
     *   optionally calls the `handleExpanded` function if it is provided.
     * - If the mode is 'add', it calls the `createClickHandler` function and then
     *   optionally calls the `handleExpanded` function if it is provided.
     * 
     * @returns {void}
     */
    const onSubmitHandler = () => {
        if (props.mode === 'edit') {
            saveClickHandler()
            if (props.handleExpanded) {
                props.handleExpanded()
            }
        }

        if (props.mode === 'add') {
            createClickHandler()
            if (props.handleExpanded) {
                props.handleExpanded()
            }
        }
    }


    useEffect(() => {
        getNonScheduledEvents();
    }, []);

    useEffect(() => {
        if (props.mode === "add") {
            setFormState(blankFormState)
        }
    }, [props.mode]);

    useEffect(() => {
        checkTimesheetExistsForDayAndEmployee()
    }, [selectedDay, props.employeeId])

    return (
        <>
            {
                loading && selectedItem === undefined ? (
                    <SkeletonForm />
                ) : (
                    <div className='form__wrapper'>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <TabStrip

                                selected={selectedTab}
                                onSelect={(e) => setSelectedTab(e.selected)}
                                className='form__tabstrip__container'
                            >
                                <TabStripTab title="Entry details">
                                    <Form onSubmit={() => onSubmitHandler()}
                                        // initialValues={{ description: formState.description }}
                                        render={(formRenderProps) => (
                                            <FormElement style={{ background: "white", borderRadius: "10px", padding: '30px 1rem 1rem 1rem', width: '100%', marginBottom: '5rem' }} >
                                                <fieldset className={'k-form-fieldset'} disabled={props.readOnly}>
                                                    {props.readOnly && <p className='k-form-hint'>You have view-only access to timesheets.</p>}

                                                    {
                                                        props.mode === "add" && (
                                                            <>
                                                                <Field
                                                                    name={'day'}
                                                                    component={FormDropDownList}
                                                                    label={'Select a day:'}
                                                                    data={props.weekDays}
                                                                    defaultItem={selectedDay}
                                                                    onChange={(e) => {
                                                                        setSelectedDay(e.value)
                                                                    }}
                                                                />
                                                                {
                                                                    !currentTimeSheet && (
                                                                        <Field
                                                                            name={'time'}
                                                                            component={FormTimePicker}
                                                                            label={'Select a start time:'}
                                                                            onChange={(e) => {
                                                                                setStartTime(e.value)
                                                                            }}
                                                                        />                                                        
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }

                                                    {
                                                        selectedJobItem && (
                                                            <label style={{ marginTop: "10px", fontSize: "10pt", color: "rgb(36, 32, 56)" }} className="k-label">Selected job:</label>
                                                        )
                                                    }
                                                        {
                                                            selectedJobItem ? (
                                                                <div className="selected-job">
                                                                    <strong>{selectedJobItem.ref}</strong> - {selectedJobItem.description}
                                                                </div>
                                                            ) : jobDataUnscheduled.length !== 0 ? (
                                                                    jobDataUnscheduled.find(item => item.jobId === formState.jobId) ? (
                                                                        <div className="selected-job">
                                                                            <strong>{jobDataUnscheduled.find(item => item.jobId === formState.jobId)?.ref}</strong> - {jobDataUnscheduled.find(item => item.jobId === formState.jobId)?.description}
                                                                            </div>
                                                                    ) : null
                                                            ) : null
                                                        }
                                                    {!props.readOnly && (
                                                    <div className={"job-space"}>
                                                        <EventContainerList
                                                            selectedItem={selectedJobItem}
                                                            setSelectedItem={(item) => {
                                                                setSelectedJobItem(item);
                                                                setFormState({ ...formState, jobId: item.jobId })
                                                            }}
                                                            onData={jobDataUnscheduled} />
                                                    </div>
                                                    )}
                                                    <Field
                                                        name={'timeSpent'}
                                                        component={FormInput}
                                                        label={'Time spent:'}
                                                        disabled={props.readOnly}
                                                        value={formState.timeSpent}
                                                        defaultValue={formState.timeSpent}
                                                        onChange={(e) => {
                                                            setFormState({ ...formState, timeSpent: e.value })
                                                        }}
                                                    />
                                                    <Field
                                                        name={'notes'}
                                                        component={FormTextArea}
                                                        label={'Notes:'}
                                                        disabled={props.readOnly}
                                                        defaultValue={formState.notes}
                                                        onChange={(e) => {
                                                            setFormState({ ...formState, notes: e.value })
                                                        }}
                                                    />

                                                    {!props.readOnly && (
                                                    <div className='button__container'>
                                                        {
                                                            props.mode === 'edit' && (
                                                                <button
                                                                    onClick={() => saveClickHandler()}
                                                                    className='form__edit__button accept'
                                                                >
                                                                    Save changes
                                                                </button>
                                                            )
                                                        }

                                                        {
                                                            props.mode === 'edit' && (
                                                                <button
                                                                    onClick={() => {
                                                                        deleteClickHandler()
                                                                        formRenderProps.onFormReset()
                                                                        if (props.handleExpanded) {
                                                                            props.handleExpanded()
                                                                        }
                                                                    }}
                                                                    className='form__edit__button reject'
                                                                >
                                                                    Delete entry
                                                                </button>
                                                            )
                                                        }

                                                        {
                                                            props.mode === "add" && (
                                                                <button
                                                                    type="submit"
                                                                    className='form__edit__button accept'
                                                                >
                                                                    Add Entry
                                                                </button>
                                                            )
                                                        }
                                                    </div>
                                                    )}

                                                </fieldset>
                                            </FormElement>
                                        )}
                                    />
                                </TabStripTab>
                            </TabStrip>
                        </div>
                    </div>
                )
            }
        </>
    )
}

export default TimesheetForm